import React, { useMemo, useRef, useState, useEffect } from "react";
import { getApiClient } from "../../libs/axiosInstance";

// Chart.js
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
  Title
);

// Helpers
const todayIsoWeekday = () => {
  const js = new Date().getDay(); // 0..6
  return js === 0 ? 7 : js;
};

const MONTHS = [
  { v: "all", label: "All time" },
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
];

function HourlySalesChart({
  // Controlled props (optional)
  weekday,
  onWeekdayChange,
  month,
  onMonthChange,
  data = [], // [{hour, amount}]
  isLoading = false,
  info = null, // { insufficient, insufficientReason, busiestHour }

  // Uncontrolled (self-fetch)
  selfFetch = false,
}) {
  // Local state when uncontrolled
  const [localWeekday, setLocalWeekday] = useState(
    weekday || todayIsoWeekday()
  );
  const [localMonth, setLocalMonth] = useState(month ?? "all");

  const effectiveWeekday = weekday ?? localWeekday;
  const effectiveMonth = month ?? localMonth;

  const [localData, setLocalData] = useState([]);
  const [localInfo, setLocalInfo] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const axiosRef = useRef(null);
  if (!axiosRef.current) axiosRef.current = getApiClient();

  // Debounced self-fetch
  const debounceRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!selfFetch) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setLocalLoading(true);
      try {
        const qs = new URLSearchParams({
          weekday: String(effectiveWeekday),
          month: String(effectiveMonth),
        }).toString();
        const resp = await axiosRef.current.get(`/api/stats/hourly?${qs}`);
        const payload = resp.data || {};
        const hours = Array.isArray(payload.hours) ? payload.hours : [];
        const map = new Map(
          hours.map((r) => [Number(r.hour), Number(r.avgAmount) || 0])
        );
        const normalized = Array.from({ length: 24 }, (_, h) => ({
          hour: h,
          amount: map.get(h) ?? 0,
        }));
        setLocalData(normalized);
        setLocalInfo({
          insufficient: !!payload.insufficient,
          insufficientReason: payload.insufficientReason || null,
          busiestHour: payload.busiestHour || null,
        });
      } catch {
        setLocalData([]);
        setLocalInfo({
          insufficient: true,
          insufficientReason: "Unable to fetch hourly averages.",
          busiestHour: null,
        });
      } finally {
        setLocalLoading(false);
        inFlightRef.current = false;
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [selfFetch, effectiveWeekday, effectiveMonth]);

  // Choose data source
  const renderData = selfFetch ? localData : data;
  const renderInfo = selfFetch ? localInfo : info;
  const renderLoading = selfFetch ? localLoading : isLoading;

  const hours = useMemo(() => renderData.map((d) => d.hour), [renderData]);
  const values = useMemo(
    () => renderData.map((d) => Number(d.amount) || 0),
    [renderData]
  );
  const busiestHour = renderInfo?.busiestHour?.hour ?? null;

  // Colors (highlight busiest bar)
  const backgroundColors = useMemo(() => {
    return renderData.map((d) =>
      d.hour === busiestHour
        ? "rgba(76, 175, 80, 0.9)"
        : "rgba(108, 122, 224, 0.85)"
    );
  }, [renderData, busiestHour]);
  const borderColors = useMemo(() => {
    return renderData.map((d) =>
      d.hour === busiestHour ? "rgba(76, 175, 80, 1)" : "rgba(108, 122, 224, 1)"
    );
  }, [renderData, busiestHour]);

  const chartData = useMemo(
    () => ({
      labels: hours.map((h) => String(h).padStart(2, "0") + ":00"),
      datasets: [
        {
          label: "Avg Amount",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.95,
          categoryPercentage: 1.0,
        },
      ],
    }),
    [hours, values, backgroundColors, borderColors]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y || 0;
              return `Avg: ${v.toLocaleString()}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            callback: (val, idx) => {
              // show every 3rd hour, plus last
              return idx % 3 === 0 || idx === 23 ? chartData.labels[idx] : "";
            },
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: {
            callback: (v) => Number(v).toLocaleString(),
          },
        },
      },
    }),
    [chartData.labels]
  );

  const handleWeekdayChange = (e) => {
    const w = parseInt(e.target.value, 10);
    if (onWeekdayChange) onWeekdayChange(w);
    else setLocalWeekday(w);
  };
  const handleMonthChange = (e) => {
    const mRaw = e.target.value;
    const m = mRaw === "all" ? "all" : parseInt(mRaw, 10);
    if (onMonthChange) onMonthChange(m);
    else setLocalMonth(m);
  };

  return (
    <div className="card avg_sales_cont" style={{ padding: 16, marginTop: 20 }}>
      <div className="avg_sales_header">
        <h5 className="m-0">Average Sales per Hour (by Weekday)</h5>
        <div
          style={{
            display: "inline-block",
            paddingLeft: "2vh",
          }}
        >
          {renderInfo?.busiestHour ? (
            <small className="text-muted d-none d-md-inline">
              Peak ~ <b>{String(busiestHour).padStart(2, "0")}:00</b>
            </small>
          ) : (
            <small className="text-muted avg_tab_divider">—</small>
          )}
        </div>
        <div
          style={{
            display: "block",
            padding: "1vh 0",
          }}
        >
          <div className="select_conts">
            {/* Month select (uniform with Leaderboard) */}
            <select
              className="form-select form-select-sm"
              value={String(effectiveMonth)}
              onChange={handleMonthChange}
              style={{ minWidth: 140 }}
              aria-label="Select month scope"
            >
              {MONTHS.map((m) => (
                <option key={m.v} value={String(m.v)}>
                  {m.label}
                </option>
              ))}
            </select>

            {/* Weekday select */}
            <select
              className="form-select form-select-sm"
              value={effectiveWeekday}
              onChange={handleWeekdayChange}
              style={{ minWidth: 150 }}
              aria-label="Select weekday"
            >
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
              <option value={7}>Sunday</option>
            </select>
          </div>
        </div>
      </div>

      {renderLoading ? (
        <div className="text-muted">Loading hourly averages…</div>
      ) : renderInfo?.insufficient ? (
        <div className="text-muted">
          {renderInfo.insufficientReason ||
            "Insufficient data to compute reliable averages for this selection."}
        </div>
      ) : !renderData.length ? (
        <div className="text-muted">No data.</div>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <Bar data={chartData} options={options} />
        </div>
      )}
    </div>
  );
}

export default HourlySalesChart;
